/* eslint-disable pixelated/class-name-kebab-case */
"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Tiles now includes built-in modal support so it can work for both direct Tiles usage
// and the shared ProjectsClient wrapper. Because this modal behavior is managed at the
// Tiles instance level, each Tiles render requires its own modal state and unique
// DOM identifier via useId() to prevent collisions when multiple Tiles sections
// are present on the same page.
import { useId, useState } from 'react';
import PropTypes from "prop-types";
import { Loading } from "../foundation/loading";
import { SmartImage } from "./smartimage";
import { Modal, handleModalOpen } from "./modal";
import { PageSection, PageSectionHeader } from "../structure/page-blocks";
import { usePixelatedConfig } from '../config/config.client';
import "../../css/pixelated.grid.scss";
import "./tiles.css";
export const TilesVariants = ['caption', 'overlay'];
/**
 * Tiles — renders a responsive grid of image tiles with optional click handlers and visual variants.
 *
 * @param {array} [props.cards] - Array of card objects to render (image, link, imageAlt, bodyText).
 * @param {number} [props.rowCount] - Number of rows to display; affects column sizing.
 * @param {function} [props.imgClick] - Optional (event, imageUrl) click handler for tile images.
 * @param {boolean} [props.modalOnClick] - When true, tile image clicks open an internal modal.
 * @param {boolean} [props.showOverlay] - Whether to render the tile overlay; defaults to true.
 * @param {oneOf} [props.variant] - Visual variant: 'caption' (caption beneath image) or 'overlay' (overlay on hover).
 */
Tiles.propTypes = {
    /** Array of card objects used to populate the tile grid (image, link, imageAlt, bodyText). */
    cards: PropTypes.arrayOf(PropTypes.shape({
        index: PropTypes.number,
        cardLength: PropTypes.number,
        link: PropTypes.string,
        image: PropTypes.string.isRequired,
        imageAlt: PropTypes.string,
        bodyText: PropTypes.string,
        imgClick: PropTypes.func,
        variant: PropTypes.oneOf(TilesVariants),
    })).isRequired,
    /** Number of rows to display in the grid (controls layout). */
    rowCount: PropTypes.number,
    /** Optional click handler for tile images; called with (event, imageUrl). */
    imgClick: PropTypes.func,
    /** Enable built-in modal behavior when a tile image is clicked. */
    modalOnClick: PropTypes.bool,
    /** Whether to render the overlay content on each tile. */
    showOverlay: PropTypes.bool,
    /**
     * Optional visual variant. Allowed values are enumerated so consumers get
     * a discoverable, typed API.
     */
    /** Visual variant for tile rendering (e.g. 'caption' or 'overlay'). */
    variant: PropTypes.oneOf(TilesVariants),
};
export function Tiles(props) {
    const rowCount = props.rowCount ?? 2;
    const modalOnClick = props.modalOnClick ?? false;
    const showOverlay = props.showOverlay ?? true;
    const [modalContent, setModalContent] = useState(undefined);
    const modalID = useId();
    const handleTileImageClick = props.imgClick ?? (modalOnClick ? (event, url) => {
        const myContent = _jsx("div", { className: "modal-image-container", children: _jsx(SmartImage, { src: url, alt: "Image preview" }) });
        setModalContent(myContent);
        handleModalOpen(event.nativeEvent, modalID);
    } : undefined);
    if (props.cards && props.cards.length > 0) {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: "tiles-container", children: _jsx("div", { className: `tile-container row-${rowCount}col`, suppressHydrationWarning: true, children: props.cards.map((card, i) => (_jsx("div", { className: "grid-item", children: _jsx(Tile, { index: card.index ?? i, cardLength: card.cardLength ?? props.cards.length, link: card.link, image: card.image, imageAlt: card.imageAlt, bodyText: card.bodyText, imgClick: handleTileImageClick, variant: (props.variant ?? "overlay"), showOverlay: showOverlay }) }, i))) }) }), _jsx(Modal, { modalID: modalID, modalContent: modalContent ?? _jsx(_Fragment, {}) })] }));
    }
    else {
        return (_jsx(Loading, {}));
    }
}
/* ========== TILE ========== */
/**
 * Tile — single grid tile that displays an image with optional link and caption/overlay.
 *
 * @param {number} [props.index] - Zero-based index for the tile (used for IDs).
 * @param {number} [props.cardLength] - Total number of tiles in the current set.
 * @param {string} [props.link] - Optional href for the whole tile.
 * @param {string} [props.image] - Image URL to display (required).
 * @param {string} [props.imageAlt] - Alt text for the image; used as caption fallback.
 * @param {string} [props.bodyText] - Optional caption or descriptive text for the tile.
 * @param {function} [props.imgClick] - Optional click handler for the image (event, imageUrl).
 * @param {oneOf} [props.variant] - Visual variant: 'caption' | 'overlay'.
 */
Tile.propTypes = {
    /** Zero-based index of the tile. */
    index: PropTypes.number.isRequired,
    /** Total number of tiles in this grid. */
    cardLength: PropTypes.number.isRequired,
    /** Optional href for this tile. */
    link: PropTypes.string,
    /** Image source URL to display (required). */
    image: PropTypes.string.isRequired,
    /** Alt text for the image; also used as caption fallback. */
    imageAlt: PropTypes.string,
    /** Optional caption or descriptive body text shown with the tile. */
    bodyText: PropTypes.string,
    /** Click handler invoked when the tile image is clicked; receives (event, imageUrl). */
    imgClick: PropTypes.func,
    /** Whether to show the overlay content for this tile. */
    showOverlay: PropTypes.bool,
    /** 'caption' - caption beneath image; 'overlay' - overlay displayed on hover. */
    variant: PropTypes.oneOf(TilesVariants),
};
function Tile(props) {
    const config = usePixelatedConfig();
    const imgClick = props.imgClick;
    const showOverlay = props.showOverlay ?? true;
    const captionText = (props.bodyText && props.bodyText.length > 0) ? props.bodyText : (props.imageAlt ?? "");
    const tileBody = _jsxs("div", { className: "tile-image" + (imgClick ? " clickable" : ""), children: [_jsx(SmartImage, { src: props.image, aboveFold: (props.index === 0) ? true : undefined, title: props?.imageAlt ?? undefined, alt: props?.imageAlt ?? "", onClick: imgClick ? (event) => imgClick(event, props.image) : undefined, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined }), showOverlay ? (_jsx("div", { className: "tile-image-overlay", children: _jsxs("div", { className: "tile-image-overlay-text", children: [_jsx("div", { className: "tile-image-overlay-title", children: props.imageAlt }), _jsx("div", { className: "tile-image-overlay-body", children: props.bodyText })] }) })) : null] });
    const rootClass = `tile${(props.variant) ? ' ' + props.variant : ''}`;
    return (_jsx("div", { className: rootClass, id: 'tile-' + props.index, suppressHydrationWarning: true, children: props.link ?
            _jsx("a", { href: props.link, className: "tile-link", children: tileBody })
            :
                tileBody }));
}
/**
 * ProjectTiles — Renders a titled section with description and a grid of project image tiles.
 *
 * @param {string} [props.title] - Title of the project section.
 * @param {string} [props.description] - Description text for the project section.
 * @param {array} [props.tileCards] - Array of tile card objects (index, cardIndex, cardLength, image, imageAlt).
 * @param {function} [props.onImageClick] - Optional click handler for tile images (event, imageUrl).
 * @param {boolean} [props.modalOnClick] - Enable built-in tile modal behavior for project images.
 * @param {boolean} [props.showOverlay] - Whether to render tile overlay text for project images.
 */
ProjectTiles.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    tileCards: PropTypes.arrayOf(PropTypes.shape({
        index: PropTypes.number.isRequired,
        cardIndex: PropTypes.number.isRequired,
        cardLength: PropTypes.number.isRequired,
        image: PropTypes.string.isRequired,
        imageAlt: PropTypes.string.isRequired,
    })).isRequired,
    onImageClick: PropTypes.func,
    modalOnClick: PropTypes.bool,
    showOverlay: PropTypes.bool,
};
export function ProjectTiles(props) {
    const { title, description, tileCards, onImageClick, modalOnClick, showOverlay } = props;
    return (_jsxs(_Fragment, { children: [_jsx("h3", { children: title }), _jsx("p", { children: description }), _jsx(Tiles, { variant: "caption", cards: tileCards, rowCount: 3, imgClick: onImageClick, modalOnClick: modalOnClick, showOverlay: showOverlay })] }));
}
/* ========== PROJECTS CLIENT ========== */
/**
 * ProjectsClient — Renders a list of projects with their respective tiles and handles modal interactions for images.
 * The page title should be added to the page component using PageTitleHeader.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.projects - An array of project objects, each containing a title, description, and an array of tile cards.
 * @returns {JSX.Element} The rendered ProjectsClient component.
 */
ProjectsClient.propTypes = {
    projects: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        tileCards: PropTypes.arrayOf(PropTypes.shape({
            index: PropTypes.number.isRequired,
            cardIndex: PropTypes.number.isRequired,
            cardLength: PropTypes.number.isRequired,
            image: PropTypes.string.isRequired,
            imageAlt: PropTypes.string,
        })).isRequired,
    })).isRequired,
    modalOnClick: PropTypes.bool,
    showOverlay: PropTypes.bool,
};
export function ProjectsClient(props) {
    const { projects, modalOnClick, showOverlay } = props;
    return (_jsx(_Fragment, { children: _jsxs(PageSection, { columns: 1, maxWidth: "1024px", padding: "20px", id: "projects-section", children: [_jsx(PageSectionHeader, { title: "Our Projects" }), projects.map((project, idx) => (_jsx("div", { style: { marginBottom: "40px" }, children: _jsx(ProjectTiles, { title: project?.title || `Project ${idx + 1}`, description: project?.description || "", tileCards: project?.tileCards.map((card, cardIdx) => ({
                            index: card?.index || cardIdx,
                            cardIndex: card?.cardIndex || cardIdx,
                            cardLength: card?.cardLength || project?.tileCards?.length || 0,
                            image: card?.image || "",
                            imageAlt: card?.imageAlt || `Project ${idx + 1} - Tile ${cardIdx + 1}`,
                        })) || [], modalOnClick: modalOnClick ?? true, showOverlay: showOverlay ?? true }) }, idx)))] }) }));
}
